import React, { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { ThemeContext } from 'styled-components';

import getRadius from 'lib/getRadius';
import overrideStyle from 'lib/overrideStyle';
import { useScript } from 'presentation/hooks/useScript';

export function SecurePaymentForm(props) {
  const theme = useContext(ThemeContext);
  const [form, setForm] = useState(null);
  const [canSubmitForm, setCanSubmitForm] = useState(false);

  const css = {
    backgroundColor: '#F0F0F0',
    borderRadius: '10px',
    boxSizing: 'border-box',
    fontSize: '20px',
    fontFamily: theme.fontLibrary.primary,
    height: '30px',
    width: '290px',
    left: '20px',
    padding: '10px',
    position: 'absolute',
    transition: 'all 0.2s ease',

    '&:focus': {
      backgroundColor: '#c5c5c5',
    },
  };

  const cvcCss = { ...css, width: '100px', fontSize: '16px' };

  useEffect(() => {
    if (!form) {
      return;
    }

    form.field('#cc-name', {
      type: 'text',
      name: 'card_name',
      placeholder: 'Nonna Joe Business',
      validations: ['required'],
      css: css,
    });

    form.field('#cc-number', {
      showCardIcon: true,
      type: 'card-number',
      name: 'card_number',
      successColor: '#4F8A10',
      errorColor: '#D8000C',
      placeholder: '4111 1111 1111 1111',
      validations: ['required', 'validCardNumber'],
      css: css,
    });

    // CVC
    form.field('#cc-cvc', {
      type: 'card-security-code',
      name: 'card_cvc',
      placeholder: '123',
      validations: ['required', 'validCardSecurityCode'],
      css: cvcCss,
    });

    // EXP
    form.field('#cc-expiration-date', {
      type: 'card-expiration-date',
      name: 'card_expirationDate',
      placeholder: '01 / 2022',
      validations: ['required', 'validCardExpirationDate'],
      css: cvcCss,
    });

    document.getElementById('cc-form').addEventListener('submit', submitFormHelper);
    return () => document.getElementById('cc-form').removeEventListener('submit', submitFormHelper);
  }, [form]);

  const submitFormHelper = useCallback(
    event => {
      event.preventDefault();
      form.submit(`/api/v1/secure-data/ccr/orgs/${props.orgId}/customers/${props.gladlyCustomerId}`);
    },
    [form]
  );

  const scriptLoadedCallback = useCallback(() => {
    const vgsForm = window.VGSCollect.create(props.vaultId, state => {
      setCanSubmitForm(Object.values(state).every(x => x.isValid));
    });

    setForm(vgsForm);
  }, []);

  useScript(`https://js.verygoodvault.com/vgs-collect/1/${props.secureOrgId}.js`, scriptLoadedCallback);

  if (!form) {
    return null;
  }

  return (
    <Wrapper>
      <CCForm id="gladly-ccform">
        <form id="cc-form">
          <div id="cc-number" />
          <div id="cc-name" />
          <HalfDiv id="cc-cvc" />
          <HalfDiv id="cc-expiration-date" />
          <Button
            canSubmitForm={canSubmitForm}
            disabled={!canSubmitForm}
            type="submit"
            className="btn btn-sm btn-primary"
          >
            {!canSubmitForm ? 'Cannot Submit Yet' : 'Submit'}
          </Button>
          <Button onClick={props.onDecline}>Decline</Button>
        </form>
      </CCForm>
      <pre id="result">Submit a form to see result.</pre>
    </Wrapper>
  );
}

SecurePaymentForm.propTypes = {
  onDecline: PropTypes.func.isRequired,
  vaultId: PropTypes.string.isRequired,
  orgId: PropTypes.string.isRequired,
  gladlyCustomerId: PropTypes.string.isRequired,
  secureOrgId: PropTypes.string.isRequired,
};

const HalfDiv = overrideStyle('div')`
  margin: 0;
  width: 50%;
`;

const Button = overrideStyle('button')`
  border-radius: ${p => getRadius(p.theme.borderRadius, 8, 16)};
  border:1px solid #E4E4E4;
  padding: 30px;
  width: 70px;

  margin: 6px 20px;
  font-family: ${p => p.theme.fontLibrary.primary};
  transition: 0.2s ease;

  &:focus{
    border: 1px solid #a3a3a3;
  }

  &:active{
    border: 1px solid #a3a3a3;
  }

  &:hover{
    border: 1px solid #a3a3a3;
    background-color: #eeeeee;
  }
`;

const CCForm = overrideStyle('div')`
  background-color: white;
  border: 1px grey solid;  
  display: flex;
  flex-direction: column;
  top: 64px;
  left: 0px;
  position: absolute;
  height: 250px;
  width: 100%;

  & iframe {
    height: 32px;
    padding: 10px;
    width: 310px;
  }
`;

const Wrapper = overrideStyle('div')`
  align-items: center;  
  border: 1px black solid;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  width: 100%;
`;
